import type {
  Commonv1Value,
  V1Audit,
  V1AutomationConfig,
} from '@/generated/hooks/grimoire/models';

export interface IResolvedStepConfig {
  /** Config map key (`kognitos.config.<name>`) — never rendered; used only
   *  to look up and write back into the automation's config map. */
  name: string;
  /** Human-readable label shown instead of `name`. */
  label: string;
  value?: Commonv1Value;
}

/** Resolves a step's `audit.config_accesses` (name-only references) against
 *  the automation's config map to get each entry's editable label + value.
 *  Accesses with no matching config entry are skipped — editing a config
 *  that doesn't exist doesn't make sense. Duplicate names (a config read
 *  more than once in the same step) collapse to a single entry. */
export function resolveStepConfigs(
  audit: V1Audit | undefined,
  automationConfig: V1AutomationConfig | undefined,
): IResolvedStepConfig[] {
  const accesses = audit?.config_accesses ?? [];
  if (!automationConfig) return [];

  const resolved: IResolvedStepConfig[] = [];
  const seen = new Set<string>();
  for (const access of accesses) {
    const name = access.name;
    if (!name || seen.has(name)) continue;
    const entry = automationConfig[name];
    if (!entry) continue;
    seen.add(name);
    resolved.push({ name, label: entry.label || name, value: entry.value });
  }
  return resolved;
}
